import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private scriptPromise: Promise<void> | null = null;

  async execute(action: string): Promise<string> {
    const siteKey = environment.recaptchaSiteKey;

    if (!siteKey) {
      throw new Error('Missing reCAPTCHA site key');
    }

    await this.loadScript(siteKey);

    return new Promise<string>((resolve, reject) => {
      window.grecaptcha.enterprise.ready(async () => {
        try {
          const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
          resolve(token);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  private loadScript(siteKey: string): Promise<void> {
    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[src*="recaptcha/enterprise.js"]'
      );

      if (existingScript) {
        if (window.grecaptcha?.enterprise) {
          resolve();
        } else {
          existingScript.addEventListener('load', () => resolve());
          existingScript.addEventListener('error', () => {
            this.scriptPromise = null;
            reject(new Error('Failed to load reCAPTCHA script'));
          });
        }
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => resolve();
      script.onerror = () => {
        this.scriptPromise = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }
}
